import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { DatePicker } from '@/components/core/date-picker/date-picker';
import { PageHeader } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { ExportCsvButton } from '@/modules/service-desk/shared/components/export-csv-button';
import { downloadCsv } from '@/lib/csv';
import { useServerTable } from '@/hooks/use-server-table';
import { mergeWhere } from '@/lib/table-query';
import { Plus } from '@phosphor-icons/react';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useIsMobile } from '@/hooks/use-media-query';
import { useTranslation } from 'react-i18next';
import { useDepartmentOptions, useEmployeesByUserIds } from '@/modules/shared/hr-lookups';
import { createFoodRequestsTableColumns } from '../../components/food-requests-table/food-requests-table-columns';
import { MenuFormDialog } from '../../components/menu-form-dialog/menu-form-dialog';
import { MenuTable } from '../../components/menu-table/menu-table';
import { DEFAULT_LUNCH_REQUESTS_ORDER } from '../../graphql/queries';
import { useGetLunchMenus, useGetLunchRequests } from '../../hooks/use-manage-lunch';
import { getAllLunchRequests } from '../../services/manage-lunch.service';
import { LunchMenusResult, LunchRequestsPagingResult } from '../../types/manage-lunch.types';
import { buildFoodRequestsCsv } from '../../utils/food-requests-csv';
import { buildLunchDayFilter, todayInOrganization } from '../../utils/lunch-date';

/** The table's filter for one day's food requests, shared by the table and the CSV export. */
const dayFilterOf = (date: string) => {
  const dayClause = buildLunchDayFilter(date);
  return dayClause ? { RequestedForDate: dayClause } : {};
};

const MenuTab = () => {
  const { data, isLoading } = useGetLunchMenus({ pageNo: 1, pageSize: 100, filter: {} });
  const menus = (data as { LunchMenus?: LunchMenusResult })?.LunchMenus?.items ?? [];

  return <MenuTable menus={menus} isLoading={isLoading} />;
};

const FoodRequestsTab = ({
  date,
  mobileActions,
}: {
  date: string;
  /** On a phone, the day picker on the cards' line, beside the filter button. */
  mobileActions?: ReactNode;
}) => {
  const { t } = useTranslation();
  const { pagination, resetPage, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_LUNCH_REQUESTS_ORDER,
  });

  // A new day starts over from the first page.
  useEffect(() => {
    resetPage();
  }, [date, resetPage]);

  const { data, isLoading, error } = useGetLunchRequests({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: mergeWhere(dayFilterOf(date), columnWhere),
    order,
  });
  const result = (data as { LunchRequests?: LunchRequestsPagingResult })?.LunchRequests;
  const items = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;

  const { byUserId } = useEmployeesByUserIds(items.map((request) => request.UserId));
  const { data: departments } = useDepartmentOptions();
  const columns = useMemo(
    () =>
      createFoodRequestsTableColumns({
        t,
        emailOf: (userId) => byUserId.get(userId)?.Email,
        departments,
      }),
    [t, byUserId, departments]
  );

  return (
    <AdvanceDataTable
      {...tableProps}
      data={items}
      columns={columns}
      isLoading={isLoading}
      error={error instanceof Error ? error : null}
      pagination={{
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
        totalCount,
      }}
      isExpandRowContent={false}
      tableClassName="min-w-[36rem]"
      mobileView="cards"
      mobileActions={mobileActions}
    />
  );
};

export const ManageLunchPage = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('requests');
  // Tab-specific controls sit on the tab row, so their state lives here rather than in the tabs.
  const [date, setDate] = useState(() => todayInOrganization());
  const [isMenuFormOpen, setIsMenuFormOpen] = useState(false);
  const isMobile = useIsMobile();
  const datePicker = (
    // On a phone it fills the cards' line, beside the (same height) filter button.
    <DatePicker className="w-full md:w-48" aria-label={t('DATE')} value={date} onChange={setDate} />
  );

  // Downloads every food request of the selected day, not just the page on screen.
  const exportFoodRequests = async () => {
    const requests = await getAllLunchRequests(dayFilterOf(date));
    downloadCsv(`Food Request List_${date}`, buildFoodRequestsCsv(requests));
  };

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader title={t('MANAGE_LUNCH')} />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <TabsList className="w-full sm:w-auto">
            <TabsTrigger value="requests" className="flex-1 sm:flex-none">
              {t('FOOD_REQUEST')}
            </TabsTrigger>
            <TabsTrigger value="menu" className="flex-1 sm:flex-none">
              {t('FOOD_MENU')}
            </TabsTrigger>
          </TabsList>
          {activeTab === 'requests' && (
            // On a phone the day picker moves onto the cards' line, beside the filter button.
            <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
              <ExportCsvButton onExport={exportFoodRequests} className="sm:w-auto" />
              {!isMobile && datePicker}
            </div>
          )}
          {activeTab === 'menu' && (
            <Button className="w-full sm:w-auto" onClick={() => setIsMenuFormOpen(true)}>
              <Plus className="size-4" />
              {t('ADD_MENU')}
            </Button>
          )}
        </div>
        <TabsContent value="requests" className="mt-4">
          <FoodRequestsTab date={date} mobileActions={isMobile ? datePicker : undefined} />
        </TabsContent>
        <TabsContent value="menu" className="mt-4">
          <MenuTab />
        </TabsContent>
      </Tabs>

      <MenuFormDialog open={isMenuFormOpen} onOpenChange={setIsMenuFormOpen} />
    </div>
  );
};
